'use client';

import { Icon } from '@/src/components/ui';
import { SafeIcon } from '@/src/components/ui/SafeIcon';
import { useBoolean } from '@/src/hooks/use-boolean';
import { cn } from '@workspace/ui/lib/utils';
import Image from 'next/image';

type Props = {
  imageUrl: string;
  title: string;
  description: string;
  tags: string[];
  isIgdbGame?: boolean;
};

export const GameBlock = ({ description, imageUrl, tags, title, isIgdbGame }: Props) => {
  const isExpanded = useBoolean(true);
  return (
    <div className="w-full">
      <button
        onClick={isExpanded.onToggle}
        type="button"
        className="w-full flex items-center justify-between transition-opacity group hover:cursor-pointer md:pb-2 px-6 md:px-0"
      >
        <div className="flex items-center md:gap-3">
          {!isExpanded.value && (
            <Image
              src={imageUrl}
              alt={`${title} cover`}
              width={28}
              height={28}
              className="size-7 object-cover rounded-sm flex-shrink-0"
              unoptimized
            />
          )}
          <h2 className="text-xl! font-bold md:inline-block hidden">
            {(isExpanded.value ? 'About ' : '') + title}
          </h2>
        </div>
        <Icon
          name="chevron-down"
          className={cn(
            'size-5 transition-transform duration-300 flex-shrink-0',
            isExpanded.value && 'rotate-180',
          )}
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isExpanded.value
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden px-6 md:px-0">
          <div className="flex md:gap-5 gap-4 items-center">
            <div
              className={cn('md:w-40 md:h-52 h-32 w-24 flex-shrink-0', {
                'md:h-40 h-24': !isIgdbGame,
              })}
            >
              <Image
                src={imageUrl}
                alt={`${title} poster`}
                width={160}
                height={208}
                className="w-full h-full object-cover rounded-sm"
                unoptimized
              />
            </div>

            <div className="flex flex-col w-fit">
              <div className="md:flex hidden mb-4 md:max-h-52 overflow-y-scroll no-scrollbar">
                <div
                  className="prose prose-sm md:prose-base font-light leading-5 text-gray-700 md:max-w-[490px] break-words"
                  dangerouslySetInnerHTML={{ __html: description ?? '' }}
                />
              </div>
              <h3 className="inline md:hidden text-lg! font-bold">{title}</h3>
              <div className="flex gap-2 flex-wrap md:mt-0 mt-3">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="md:text-sm text-xs bg-black/5 px-2 py-1 rounded-[6px] flex items-center md:h-7 h-6 border"
                  >
                    <SafeIcon
                      name={tag.toLowerCase()}
                      fallback="filler"
                      className="size-4 mr-1"
                    />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
